// ═══════════════════════════════════════════════════════════
// FOIA — Request API
//
// All data operations for the Request lifecycle.
// Tables involved: requests, requesters, clockPauses, auditLog
// ═══════════════════════════════════════════════════════════

import {
  queryRecords, createRecord, updateRecord, fetchRecords, fetchRecord,
} from './client';
import { TABLES } from './tables';
import { FIELDS } from './fields';
import { evaluateClock, buildHolidaySet } from '../lib/deadlineClock';
import { listHolidays, buildHolidaySet as buildHolidaySetFromRecords } from './holidays';
import { sendCorrespondence, applyMergeFields, BUILT_IN_TEMPLATES } from './correspondence';
import { classifyRequest, type AIClassificationResult } from './ai';

// ── Domain types ─────────────────────────────────────────

export type RequestStatus =
  | 'draft' | 'submitted' | 'acknowledged' | 'inProgress'
  | 'awaitingRequester' | 'awaitingPayment' | 'pendingReview'
  | 'completed' | 'denied' | 'withdrawn' | 'onHold';

export type RequestPriority = 'normal' | 'high' | 'urgent';
export type RequestChannel = 'portal' | 'email' | 'mail' | 'inPerson' | 'phone';
export type RequesterType = 'individual' | 'business' | 'media' | 'attorney' | 'government' | 'other';

export interface Requester {
  recordId: number;
  type: RequesterType;
  firstName: string;
  lastName: string;
  orgName?: string;
  email?: string;
  phone?: string;
  address?: string;
  isVerified: boolean;
  isRegistered: boolean;
  publicUserId?: string;
  internalNotes?: string;
  // computed
  displayName: string;
}

export interface FoiaRequest {
  recordId: number;
  requestNumber: string;
  status: RequestStatus;
  requestType: string;
  requesterId?: number;
  assignedUserId?: string;
  assignedDeptId?: string;
  subject: string;
  description: string;
  dateSubmitted: string;          // ISO date string
  dateAcknowledged?: string;
  statutoryDeadline?: string;
  extensionDeadline?: string;
  extensionReason?: string;
  clockPausedAt?: string;
  clockPausedReason?: string;
  totalPausedDays: number;
  effectiveDeadline?: string;
  priority: RequestPriority;
  requestChannel: RequestChannel;
  isAnonymous: boolean;
  tags?: string;
  jurisdictionId?: string;
  workflowId?: string;
  invoiceId?: string;
  totalFeesEstimated?: number;
  totalFeesCharged?: number;
  totalFeesPaid?: number;
  internalNotes?: string;
  closedAt?: string;
  closureReason?: string;
  // AI fields
  aiDeptSuggestion?: string;
  aiComplexity?: string;
  aiFlags?: string;
  // computed from deadline engine
  daysRemaining?: number;
  clockStatus?: string;
  isOverdue?: boolean;
  isPaused?: boolean;
}

// ── Row → domain mapper ──────────────────────────────────

const F = FIELDS;

function rowToRequester(row: Record<string, unknown>): Requester {
  const firstName = String(row[F.requesters.firstName] ?? '');
  const lastName = String(row[F.requesters.lastName] ?? '');
  const orgName = row[F.requesters.orgName] as string | undefined;
  const type = (row[F.requesters.type] as RequesterType) ?? 'individual';

  let displayName = [firstName, lastName].filter(Boolean).join(' ');
  if (!displayName && orgName) displayName = orgName;
  if (!displayName) displayName = 'Anonymous';

  return {
    recordId: row['record_id'] as number,
    type,
    firstName,
    lastName,
    orgName,
    email: row[F.requesters.email] as string | undefined,
    phone: row[F.requesters.phone] as string | undefined,
    address: row[F.requesters.address] as string | undefined,
    isVerified: row[F.requesters.isVerified] === true || row[F.requesters.isVerified] === 'true',
    isRegistered: row[F.requesters.isRegistered] === true || row[F.requesters.isRegistered] === 'true',
    publicUserId: row[F.requesters.publicUserId] as string | undefined,
    internalNotes: row[F.requesters.internalNotes] as string | undefined,
    displayName,
  };
}

function rowToRequest(
  row: Record<string, unknown>,
  holidays: Set<string> = new Set(),
  statutoryDays = 20,
  businessDaysOnly = true,
): FoiaRequest {
  const dateSubmitted = String(row[F.requests.dateSubmitted] ?? '');
  const totalPausedDays = Number(row[F.requests.totalPausedDays] ?? 0);
  const statutoryDeadlineStr = row[F.requests.statutoryDeadline] ? String(row[F.requests.statutoryDeadline]) : null;
  const extensionDeadlineStr = row[F.requests.extensionDeadline] ? String(row[F.requests.extensionDeadline]) : null;
  const extensionDays = (statutoryDeadlineStr && extensionDeadlineStr)
    ? Math.max(0, Math.round((new Date(extensionDeadlineStr).getTime() - new Date(statutoryDeadlineStr).getTime()) / 86400000))
    : 0;
  const clockPausedAt = row[F.requests.clockPausedAt] ? String(row[F.requests.clockPausedAt]) : null;
  const status = (row[F.requests.status] as RequestStatus) ?? 'submitted';
  const isClosed = ['completed', 'denied', 'withdrawn'].includes(status);

  let clockComputed: { daysRemaining?: number; clockStatus?: string; isOverdue?: boolean; isPaused?: boolean } = {};
  if (dateSubmitted) {
    try {
      const result = evaluateClock({
        dateSubmitted: new Date(dateSubmitted),
        statutoryDays,
        businessDaysOnly,
        holidays,
        extensionDays,
        totalPausedDays,
        clockPausedAt: clockPausedAt ? new Date(clockPausedAt) : null,
        isClosed,
      });
      clockComputed = {
        daysRemaining: result.daysRemaining,
        clockStatus: result.status,
        isOverdue: result.isOverdue,
        isPaused: result.isPaused,
      };
    } catch { /* invalid date — skip clock */ }
  }

  return {
    recordId: row['record_id'] as number,
    requestNumber: String(row[F.requests.requestNumber] ?? ''),
    status,
    requestType: String(row[F.requests.requestType] ?? 'FOIA'),
    requesterId: row[F.requests.requesterId] != null && row[F.requests.requesterId] !== '' ? Number(row[F.requests.requesterId]) : undefined,
    assignedUserId: row[F.requests.assignedUserId] as string | undefined,
    assignedDeptId: row[F.requests.assignedDeptId] as string | undefined,
    subject: String(row[F.requests.subject] ?? ''),
    description: String(row[F.requests.description] ?? ''),
    dateSubmitted,
    dateAcknowledged: row[F.requests.dateAcknowledged] as string | undefined,
    statutoryDeadline: row[F.requests.statutoryDeadline] as string | undefined,
    extensionDeadline: row[F.requests.extensionDeadline] as string | undefined,
    extensionReason: row[F.requests.extensionReason] as string | undefined,
    clockPausedAt: clockPausedAt ?? undefined,
    clockPausedReason: row[F.requests.clockPausedReason] as string | undefined,
    totalPausedDays,
    effectiveDeadline: row[F.requests.effectiveDeadline] as string | undefined,
    priority: (row[F.requests.priority] as RequestPriority) ?? 'normal',
    requestChannel: (row[F.requests.requestChannel] as RequestChannel) ?? 'portal',
    isAnonymous: row[F.requests.isAnonymous] === true || row[F.requests.isAnonymous] === 'true',
    tags: row[F.requests.tags] as string | undefined,
    jurisdictionId: row[F.requests.jurisdictionId] as string | undefined,
    workflowId: row[F.requests.workflowId] as string | undefined,
    invoiceId: row[F.requests.invoiceId] as string | undefined,
    totalFeesEstimated: row[F.requests.totalFeesEstimated] as number | undefined,
    totalFeesCharged: row[F.requests.totalFeesCharged] as number | undefined,
    totalFeesPaid: row[F.requests.totalFeesPaid] as number | undefined,
    internalNotes: row[F.requests.internalNotes] as string | undefined,
    closedAt: row[F.requests.closedAt] as string | undefined,
    closureReason: row[F.requests.closureReason] as string | undefined,
    aiDeptSuggestion: row[F.requests.aiDeptSuggestion] as string | undefined,
    aiComplexity: row[F.requests.aiComplexity] as string | undefined,
    aiFlags: row[F.requests.aiFlags] as string | undefined,
    ...clockComputed,
  };
}

// ── Queries ──────────────────────────────────────────────

export interface ListRequestsOptions {
  status?: RequestStatus | RequestStatus[];
  departmentId?: string;
  assignedUserId?: string;
  priority?: RequestPriority;
  search?: string;
  includeClosd?: boolean;
  limit?: number;
  offset?: number;
}

export async function listRequests(opts: ListRequestsOptions = {}): Promise<FoiaRequest[]> {
  const filters: import('./types').Filter[] = [];

  // Server-side filters
  if (opts.departmentId) {
    filters.push({ field: F.requests.assignedDeptId, operator: 'eq', value: opts.departmentId });
  }
  if (opts.status && !Array.isArray(opts.status)) {
    filters.push({ field: F.requests.status, operator: 'eq', value: opts.status });
  }
  if (opts.priority) {
    filters.push({ field: F.requests.priority, operator: 'eq', value: opts.priority });
  }
  if (opts.assignedUserId) {
    filters.push({ field: F.requests.assignedUserId, operator: 'eq', value: opts.assignedUserId });
  }
  if (opts.search) {
    // Best-effort: push subject contains to server; requestNumber/description are still checked client-side
    filters.push({ field: F.requests.subject, operator: 'contains', value: opts.search });
  }

  const [{ records }, holidayRecords] = await Promise.all([
    queryRecords(TABLES.requests, {
      sort: { field: F.requests.dateSubmitted, direction: 'desc' },
      limit: opts.limit ?? 200,
      ...(filters.length ? { filters } : {}),
    }),
    listHolidays(import.meta.env.VITE_JURISDICTION_ID ?? '1').catch(() => []),
  ]);

  const holidaySet = buildHolidaySetFromRecords(holidayRecords);
  let results = records.map(r => rowToRequest(r as Record<string, unknown>, holidaySet));

  // Client-side filters — kept here because GAB has no `ne` array support (closed status exclusion),
  // no multi-value `in` operator (status array), and search must also cover requestNumber and
  // description which cannot be combined server-side.
  if (!opts.includeClosd) {
    results = results.filter(r => !['completed', 'denied', 'withdrawn'].includes(r.status));
  }
  if (opts.status && Array.isArray(opts.status)) {
    results = results.filter(r => (opts.status as RequestStatus[]).includes(r.status));
  }
  if (opts.search) {
    const q = opts.search.toLowerCase();
    results = results.filter(r =>
      r.requestNumber.toLowerCase().includes(q) ||
      r.subject.toLowerCase().includes(q) ||
      r.description.toLowerCase().includes(q)
    );
  }

  return results;
}

export async function getRequest(recordId: number): Promise<FoiaRequest | null> {
  const row = await fetchRecord<Record<string, unknown>>(TABLES.requests, recordId);
  return row ? rowToRequest(row) : null;
}

export async function getRequestByNumber(requestNumber: string): Promise<FoiaRequest | null> {
  const { records } = await queryRecords(TABLES.requests, {
    filters: [{ field: F.requests.requestNumber, operator: 'eq', value: requestNumber }],
    limit: 2000,
  }, { anonymous: true });
  const row = (records as Record<string, unknown>[])[0];
  return row ? rowToRequest(row) : null;
}

export async function getRequester(recordId: number): Promise<Requester | null> {
  const row = await fetchRecord<Record<string, unknown>>(TABLES.requesters, recordId);
  return row ? rowToRequester(row) : null;
}

/** Fetch multiple requesters by ID in as few calls as possible. */
export async function getRequestersByIds(ids: number[]): Promise<Map<number, Requester>> {
  if (!ids.length) return new Map();
  const { records } = await fetchRecords(TABLES.requesters, { limit: 500 });
  // Normalize to strings for comparison — GAB may return record_id as number or string
  const idSet = new Set(ids.map(String));
  const map = new Map<number, Requester>();
  for (const row of records as Record<string, unknown>[]) {
    const id = Number(row['record_id']);
    if (idSet.has(String(id))) map.set(id, rowToRequester(row));
  }
  return map;
}

// ── Mutations ────────────────────────────────────────────

export interface CreateRequestInput {
  // Requester
  requesterFirstName: string;
  requesterLastName?: string;
  requesterEmail?: string;
  requesterPhone?: string;
  requesterOrg?: string;
  requesterType: RequesterType;

  // Request
  subject: string;
  description: string;
  dateReceived: string;           // YYYY-MM-DD
  channel: RequestChannel;
  priority?: RequestPriority;
  departmentId?: string;
  assignedUserId?: string;
  jurisdictionId?: string;
  requestType?: string;
  internalNotes?: string;
  isAnonymous?: boolean;
  isRegistered?: boolean;
  publicUserId?: string;
}

// Generate non-guessable request number: YYYY-XXXXXX (6 alphanumeric chars)
// Uses characters that avoid ambiguity (no 0/O, 1/I/L)
const REQUEST_CHARS = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

function generateRequestNumber(): string {
  const year = new Date().getFullYear();
  const arr = crypto.getRandomValues(new Uint8Array(6));
  const code = Array.from(arr, b => REQUEST_CHARS[b % REQUEST_CHARS.length]).join('');
  return `${year}-${code}`;
}

function addBusinessDays(from: Date, days: number): Date {
  let count = 0;
  const d = new Date(from);
  while (count < days) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) count++;
  }
  return d;
}

/**
 * Create a new FOIA request (and its requester record).
 * This is called from both the staff intake form and the public portal.
 *
 * For portal submissions: pass anonymous: true — the v2proxy
 * will inject the admin token so no auth is required.
 */
export async function createRequest(
  input: CreateRequestInput,
  opts?: { anonymous?: boolean; statutoryDays?: number; businessDaysOnly?: boolean },
): Promise<{ request: Record<string, unknown>; requester: Record<string, unknown> }> {
  const anonymous = opts?.anonymous ?? false;
  const statutoryDays = opts?.statutoryDays ?? 20;
  const businessDaysOnly = opts?.businessDaysOnly ?? true;

  const dateSubmitted = input.dateReceived;
  const submittedDate = new Date(dateSubmitted);

  // Calculate statutory deadline
  const deadlineDate = businessDaysOnly
    ? addBusinessDays(submittedDate, statutoryDays)
    : new Date(submittedDate.getTime() + statutoryDays * 86400000);
  const statutoryDeadline = deadlineDate.toISOString().split('T')[0];

  // Create requester record first
  const requesterRow: Record<string, unknown> = {
    [F.requesters.type]:       input.requesterType,
    [F.requesters.firstName]:  input.requesterFirstName,
    [F.requesters.lastName]:   input.requesterLastName ?? '',
    [F.requesters.orgName]:    input.requesterOrg ?? '',
    [F.requesters.email]:      input.requesterEmail ?? '',
    [F.requesters.phone]:      input.requesterPhone ?? '',
    [F.requesters.isVerified]: false,
    [F.requesters.isRegistered]: input.isRegistered ?? false,
    ...(input.publicUserId ? { [F.requesters.publicUserId]: input.publicUserId } : {}),
  };

  const requester = await createRecord(TABLES.requesters, requesterRow, { anonymous });

  // Create request record
  const requestNumber = generateRequestNumber();
  const requestRow: Record<string, unknown> = {
    [F.requests.requestNumber]:   requestNumber,
    [F.requests.status]:          'submitted',
    [F.requests.requestType]:     input.requestType ?? 'FOIA',
    [F.requests.requesterId]:     (requester as Record<string, unknown>)['record_id'],
    [F.requests.subject]:         input.subject,
    [F.requests.description]:     input.description,
    [F.requests.dateSubmitted]:   dateSubmitted,
    [F.requests.statutoryDeadline]: statutoryDeadline,
    [F.requests.effectiveDeadline]: statutoryDeadline,
    [F.requests.totalPausedDays]: 0,
    [F.requests.priority]:        input.priority ?? 'normal',
    [F.requests.requestChannel]:  input.channel,
    [F.requests.isAnonymous]:     input.isAnonymous ?? false,
    [F.requests.assignedUserId]:  input.assignedUserId ?? '',
    [F.requests.assignedDeptId]:  input.departmentId ?? '',
    [F.requests.jurisdictionId]:  input.jurisdictionId ?? '',
    [F.requests.internalNotes]:   input.internalNotes ?? '',
  };

  const request = await createRecord(TABLES.requests, requestRow, { anonymous });

  const requestRecordId = Number((request as Record<string, unknown>)['record_id']);

  // Write initial audit log entry
  try {
    await createRecord(TABLES.auditLog, {
      [F.auditLog.requestId]:   requestRecordId,
      [F.auditLog.action]:      'request.created',
      [F.auditLog.details]:     JSON.stringify({ channel: input.channel, anonymous: input.isAnonymous }),
      [F.auditLog.timestamp]:   new Date().toISOString(),
    }, { anonymous });
  } catch { /* audit log failure should not block the request */ }

  // Fire-and-forget: auto-acknowledgment email to requester
  if (input.requesterEmail) {
    const ackTemplate = BUILT_IN_TEMPLATES.find(t => t.trigger === 'onSubmission');
    if (ackTemplate) {
      const requesterName = [input.requesterFirstName, input.requesterLastName].filter(Boolean).join(' ') || 'Requester';
      const ctx = {
        requestNumber,
        subject: input.subject,
        requesterName,
        requesterEmail: input.requesterEmail,
        deadline: statutoryDeadline,
      };
      sendCorrespondence({
        requestId:   requestRecordId,
        direction:   'outbound',
        toAddresses: [input.requesterEmail],
        subject:     applyMergeFields(ackTemplate.subject, ctx),
        body:        applyMergeFields(ackTemplate.body, ctx),
        isAutomated: true,
        anonymous,
      }).catch(() => { /* best-effort */ });
    }
  }

  // Fire-and-forget: AI classification → store results on the record
  classifyRequest({
    subject:     input.subject,
    description: input.description,
    channel:     input.channel,
  }).then(result => updateAiClassification(requestRecordId, result))
    .catch(() => { /* best-effort */ });

  return { request, requester };
}

// Store AI classification results back on the request record (best-effort).
export async function updateAiClassification(
  recordId: number,
  result: AIClassificationResult,
): Promise<void> {
  await updateRecord(TABLES.requests, recordId, {
    [F.requests.aiDeptSuggestion]: result.suggestedDepartment,
    [F.requests.aiComplexity]:     result.complexity,
    [F.requests.aiFlags]:          JSON.stringify(result.flags),
  });
}

export interface UpdateRequestInput {
  status?: RequestStatus;
  assignedUserId?: string;
  assignedDeptId?: string;
  priority?: RequestPriority;
  internalNotes?: string;
  closureReason?: string;
  extensionDays?: number;
  extensionReason?: string;
  subject?: string;
  description?: string;
  requestType?: string;
  channel?: RequestChannel;
}

export async function updateRequest(
  recordId: number,
  input: UpdateRequestInput,
  actorEmail?: string,
): Promise<Record<string, unknown>> {
  const patch: Record<string, unknown> = {};

  if (input.status !== undefined)        patch[F.requests.status]           = input.status;
  if (input.assignedUserId !== undefined) patch[F.requests.assignedUserId]  = input.assignedUserId;
  if (input.assignedDeptId !== undefined) patch[F.requests.assignedDeptId]  = input.assignedDeptId;
  if (input.priority !== undefined)       patch[F.requests.priority]         = input.priority;
  if (input.internalNotes !== undefined)  patch[F.requests.internalNotes]    = input.internalNotes;
  if (input.subject !== undefined)        patch[F.requests.subject]          = input.subject;
  if (input.description !== undefined)    patch[F.requests.description]      = input.description;
  if (input.requestType !== undefined)    patch[F.requests.requestType]      = input.requestType;
  if (input.channel !== undefined)        patch[F.requests.requestChannel]   = input.channel;

  if (input.status === 'completed' || input.status === 'denied' || input.status === 'withdrawn') {
    patch[F.requests.closedAt]       = new Date().toISOString();
    patch[F.requests.closureReason]  = input.closureReason ?? '';
  }

  if (input.status === 'acknowledged' && !patch[F.requests.dateAcknowledged]) {
    patch[F.requests.dateAcknowledged] = new Date().toISOString().split('T')[0];
  }

  const updated = await updateRecord(TABLES.requests, recordId, patch);

  // Audit
  try {
    await createRecord(TABLES.auditLog, {
      [F.auditLog.requestId]:   recordId,
      [F.auditLog.userEmail]:   actorEmail ?? '',
      [F.auditLog.action]:      `request.updated`,
      [F.auditLog.details]:     JSON.stringify(patch),
      [F.auditLog.timestamp]:   new Date().toISOString(),
    });
  } catch { /* don't block on audit */ }

  return updated as Record<string, unknown>;
}

// ── Extension ────────────────────────────────────────────

/**
 * Grant a statutory deadline extension on a request.
 * Computes the new deadline by adding `days` calendar days to the
 * statutory deadline, then saves extensionDeadline, extensionReason,
 * and effectiveDeadline so the clock reflects the change immediately.
 */
export async function grantExtension(
  recordId: number,
  days: number,
  reason: string,
  statutoryDeadline: string,
  actorEmail?: string,
): Promise<void> {
  const base = new Date(statutoryDeadline);
  base.setDate(base.getDate() + days);
  const newDeadline = base.toISOString().split('T')[0];

  await updateRecord(TABLES.requests, recordId, {
    [F.requests.extensionDeadline]: newDeadline,
    [F.requests.extensionReason]:   reason,
    [F.requests.effectiveDeadline]: newDeadline,
  });

  try {
    await createRecord(TABLES.auditLog, {
      [F.auditLog.requestId]:  recordId,
      [F.auditLog.userEmail]:  actorEmail ?? '',
      [F.auditLog.action]:     'clock.extended',
      [F.auditLog.details]:    JSON.stringify({ days, reason, newDeadline }),
      [F.auditLog.timestamp]:  new Date().toISOString(),
    });
  } catch { /* don't block */ }
}

// ── Maintenance ──────────────────────────────────────────

/**
 * One-time migration: set priority = 'normal' on any request where
 * the priority field is empty or missing. Safe to run multiple times.
 * Returns the number of records patched.
 */
export async function backfillPriority(): Promise<number> {
  const { records } = await queryRecords(TABLES.requests, { limit: 1000 });
  const rows = records as Record<string, unknown>[];
  const toFix = rows.filter(r => !r[F.requests.priority] || r[F.requests.priority] === '');
  for (const row of toFix) {
    await updateRecord(TABLES.requests, row['record_id'] as number, {
      [F.requests.priority]: 'normal',
    });
  }
  return toFix.length;
}

// ── Clock operations ─────────────────────────────────────

export async function pauseClock(
  recordId: number,
  reason: string,
  notes: string,
  actorEmail?: string,
): Promise<void> {
  const now = new Date().toISOString();

  // Mark the request clock as paused
  await updateRecord(TABLES.requests, recordId, {
    [F.requests.clockPausedAt]:     now,
    [F.requests.clockPausedReason]: reason,
    [F.requests.status]:            reason === 'awaitingRequester' ? 'awaitingRequester' : 'awaitingPayment',
  });

  // Create a clock pause record for the audit trail
  await createRecord(TABLES.clockPauses, {
    [F.clockPauses.requestId]: recordId,
    [F.clockPauses.pausedAt]:  now,
    [F.clockPauses.reason]:    reason,
    [F.clockPauses.notes]:     notes,
    [F.clockPauses.pausedBy]:  actorEmail ?? '',
  });

  // Audit
  try {
    await createRecord(TABLES.auditLog, {
      [F.auditLog.requestId]: recordId,
      [F.auditLog.userEmail]: actorEmail ?? '',
      [F.auditLog.action]:    'clock.paused',
      [F.auditLog.details]:   JSON.stringify({ reason, notes }),
      [F.auditLog.timestamp]: now,
    });
  } catch { /* don't block */ }
}

export async function resumeClock(
  requestRecordId: number,
  actorEmail?: string,
): Promise<void> {
  const now = new Date();

  // Get the request to read clockPausedAt and accumulated paused days
  const reqRow = await fetchRecord<Record<string, unknown>>(TABLES.requests, requestRecordId);
  const pausedAtRaw = reqRow?.[F.requests.clockPausedAt];
  const pausedAt = pausedAtRaw ? new Date(String(pausedAtRaw)) : now;
  const currentPausedDays = Number(reqRow?.[F.requests.totalPausedDays] ?? 0);
  const pausedDaysIncrement = Math.max(0, Math.floor((now.getTime() - pausedAt.getTime()) / 86400000));
  const newTotalPausedDays = currentPausedDays + pausedDaysIncrement;

  // Find the open clock-pause record for this request and close it
  try {
    const { records } = await queryRecords(TABLES.clockPauses, {
      filters: [{ field: F.clockPauses.requestId, operator: 'eq', value: requestRecordId }],
      limit: 200,
    });
    const openPause = (records as Record<string, unknown>[])
      .filter(r => !r[F.clockPauses.resumedAt])
      .sort((a, b) =>
        new Date(String(b[F.clockPauses.pausedAt])).getTime() -
        new Date(String(a[F.clockPauses.pausedAt])).getTime()
      )[0];
    if (openPause) {
      await updateRecord(TABLES.clockPauses, openPause['record_id'] as number, {
        [F.clockPauses.resumedAt]:  now.toISOString(),
        [F.clockPauses.pausedDays]: pausedDaysIncrement,
      });
    }
  } catch { /* don't block resume if audit record update fails */ }

  // Resume the request clock
  await updateRecord(TABLES.requests, requestRecordId, {
    [F.requests.clockPausedAt]:     null,
    [F.requests.clockPausedReason]: '',
    [F.requests.totalPausedDays]:   newTotalPausedDays,
    [F.requests.status]:            'inProgress',
  });

  // Audit
  try {
    await createRecord(TABLES.auditLog, {
      [F.auditLog.requestId]: requestRecordId,
      [F.auditLog.userEmail]: actorEmail ?? '',
      [F.auditLog.action]:    'clock.resumed',
      [F.auditLog.details]:   JSON.stringify({ pausedDaysAdded: pausedDaysIncrement, totalPausedDays: newTotalPausedDays }),
      [F.auditLog.timestamp]: now.toISOString(),
    });
  } catch { /* don't block */ }
}

// ── Public portal track ──────────────────────────────────

/**
 * Track a request by request number (public portal — no auth required).
 * Only returns fields safe for public display.
 */
export interface PublicTrackResult {
  recordId: number;
  requestNumber: string;
  subject: string;
  status: RequestStatus;
  dateSubmitted: string;
  dateAcknowledged?: string;
  effectiveDeadline?: string;
  daysRemaining?: number;
  isPaused?: boolean;
  isOverdue?: boolean;
  department?: string;
}

export async function trackRequestPublic(requestNumber: string): Promise<PublicTrackResult | null> {
  const { records } = await queryRecords(TABLES.requests, {
    filters: [{ field: F.requests.requestNumber, operator: 'eq', value: requestNumber }],
    limit: 2000,
  }, { anonymous: true });

  const row = (records as Record<string, unknown>[])[0];
  if (!row) return null;

  const mapped = rowToRequest(row);
  return {
    recordId: row['record_id'] as number,
    requestNumber: mapped.requestNumber,
    subject: mapped.subject,
    status: mapped.status,
    dateSubmitted: mapped.dateSubmitted,
    dateAcknowledged: mapped.dateAcknowledged,
    effectiveDeadline: mapped.effectiveDeadline,
    daysRemaining: mapped.daysRemaining,
    isPaused: mapped.isPaused,
    isOverdue: mapped.isOverdue,
    department: mapped.assignedDeptId,
  };
}
